import React, { useState } from 'react';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { Box, AppBar, Toolbar, Typography, IconButton, Tooltip, Drawer, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import InfoIcon from '@mui/icons-material/Info';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { lightTheme, darkTheme } from './theme';
import Sidebar from './components/Sidebar';
import PetroleumView from './components/petroleum/PetroleumView';
import PlasticView from './components/plastic/PlasticView';
import ChemicalView from './components/chemical/ChemicalView';
import BiologicalView from './components/pharmaceutical/PharmaceuticalView';
import PharmaceuticalView from './components/pharmaceutical/PharmaceuticalView';
import FoodView from './components/food/FoodView';
import MiningView from './components/mining/MiningView';
import PulpPaperView from './components/pulp&paper/PulpPaperView';
import AirView from './components/air/AirView';

const AppContent: React.FC = () => {
  const [activeIndustry, setActiveIndustry] = useState('petroleum');
  const [darkMode, setDarkMode] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleIndustryChange = (industryId: string) => {
    setActiveIndustry(industryId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDarkModeToggle = () => {
    setDarkMode(!darkMode);
  };

  const renderIndustryView = () => {
    switch (activeIndustry) {
      case 'petroleum':
        return <PetroleumView />;
      case 'plastic':
        return <PlasticView />;
      case 'chemical':
        return <ChemicalView />;
      case 'biological':
        return <BiologicalView />;
      case 'pharmaceutical':
        return <PharmaceuticalView />;
      case 'food':
        return <FoodView />;
      case 'mining':
        return <MiningView />;
      case 'pulp&paper':
        return <PulpPaperView />;
      case 'air':
        return <AirView />;
      default:
        return <PetroleumView />;
    }
  };

  const sidebarContent = (
    <Sidebar
      activeIndustry={activeIndustry}
      onIndustryChange={handleIndustryChange}
      isMobile={isMobile}
    />
  );

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
        {!isMobile ? (
          sidebarContent
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
            }}
          >
            {sidebarContent}
          </Drawer>
        )}
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: isMobile ? '100%' : 'calc(100% - 280px)' }}>
          <AppBar position="static" sx={{ backgroundColor: darkMode ? '#1b5e20' : '#2e7d32' }}>
            <Toolbar>
              {isMobile && (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Paperfancy——PDR（流程工业主要技术路线）
              </Typography>
              <Tooltip title={darkMode ? "切换到亮色模式" : "切换到暗黑模式"}>
                <IconButton color="inherit" onClick={handleDarkModeToggle}>
                  {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="关于流程工业主要技术路线">
                <IconButton
                  color="inherit"
                  onClick={() => window.open('https://www.paperfancy.cn', '_blank')}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {renderIndustryView()}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={lightTheme}>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;
